import { addHours, format, startOfDay, startOfHour, startOfMonth, sub } from 'date-fns';
import { useMemo } from 'react';
import { LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { FundingRate, useFundingRates } from '../../hooks/useFundingHistory';
import { useProjectedFunding } from '../../hooks/useProjectedFunding';
import { formatPercentage } from '../../lib/formatters';
import { Gwei } from '../../lib/gwei';
import { Alert } from '../Alert';
import { FundingRateBreakdown } from '../FundingRateBreakdown/FundingRateBreakdown';
import { FundingChartSkeleton } from './FundingChartSkeleton';
import { FundingChartTooltip } from './FundingChartTooltip';
import { lineWithDashedEnd } from './lineWithDashes';

export const FundingHistoryUi = ({
  fundingRates,
  isLoading,
  error,
  timescale,
  fundingRateProjected1h,
  fundingRateProjected8h,
  fundingRateProjected1d,
  fundingRateProjected1y,
}: {
  fundingRates: FundingRate[];
  isLoading: boolean;
  error: Error | null;
  timescale: 'DAY' | 'WEEK' | 'MONTH';
  fundingRateProjected1h: bigint | undefined;
  fundingRateProjected8h: bigint | undefined;
  fundingRateProjected1d: bigint | undefined;
  fundingRateProjected1y: bigint | undefined;
}) => {
  const [dateFormat] = useMemo(() => {
    const now = new Date();

    switch (timescale) {
      case 'DAY': {
        const hours = new Array(12)
          .fill(0)
          .map((_, i) => startOfHour(sub(now, { hours: i * 2 })).getTime())
          .reverse();
        return ['h a', hours]; // 12-hour ticks
      }

      case 'WEEK': {
        const days = new Array(7)
          .fill(0)
          .map((_, i) => startOfDay(sub(now, { days: i })).getTime())
          .reverse();
        return ['MMM d', days]; // Daily ticks
      }

      case 'MONTH': {
        const months = new Array(12)
          .fill(0)
          .map((_, i) => startOfMonth(sub(now, { months: i })).getTime())
          .reverse();
        return ['MMM d', months]; // Monthly ticks
      }

      default:
        return ['h a', []];
    }
  }, [timescale]);

  const chartData = useMemo(() => {
    if (!fundingRates || fundingRates.length === 0) {
      return [];
    }

    const historicalData = fundingRates.map((x) => ({
      ...x,
      fundingRate1h: Gwei.fromGwei(x.fundingRate1h),
      isProjected: false,
    }));

    const lastDataPoint = historicalData.at(-1);
    // If we have projected data, add a point for the next hour
    if (fundingRateProjected1h !== undefined && lastDataPoint) {
      const nextHourTimestamp = addHours(new Date(lastDataPoint.timestamp), 1).getTime();

      return [
        ...historicalData,
        {
          timestamp: nextHourTimestamp,
          fundingRate1h: Gwei.fromGwei(fundingRateProjected1h),
          isProjected: true,
        },
      ];
    }

    return historicalData;
  }, [fundingRates, fundingRateProjected1h]);

  if (isLoading && fundingRates.length === 0) {
    return <FundingChartSkeleton />;
  }

  if (error || !fundingRates) {
    return (
      <Alert className="m-4 mx-auto w-fit" variant="error">
        Failed to load funding rates.
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-2xl p-4">
      <div className="flex flex-col items-center justify-end md:flex-row">
        <FundingRateBreakdown
          fundingRateProjected1h={fundingRateProjected1h}
          fundingRateProjected8h={fundingRateProjected8h}
          fundingRateProjected1d={fundingRateProjected1d}
          fundingRateProjected1y={fundingRateProjected1y}
        />
      </div>

      <ResponsiveContainer>
        <LineChart data={chartData}>
          <XAxis
            dataKey="timestamp"
            tickFormatter={(v) => format(new Date(v), dateFormat)}
            axisLine={false}
            tick={{ fill: 'var(--content-contentSecondary)', dy: 10, fontSize: '10px' }}
            minTickGap={75}
            tickLine={false}
          />
          <YAxis
            dataKey="fundingRate1h"
            tickFormatter={(v) => formatPercentage(Gwei.toGwei(v), { maximumFractionDigits: 4 })}
            axisLine={false}
            tickLine={false}
            tick={{ fill: 'var(--content-contentSecondary)', dx: -5, fontSize: '10px' }}
          />
          <Tooltip content={<FundingChartTooltip />} />
          {lineWithDashedEnd({
            dashArrayIndex: -1,
            type: 'monotone',
            dot: false,
            activeDot: { r: 4, strokeWidth: 0, fill: 'var(--content-contentPrimary)' },
            dataKey: 'fundingRate1h',
            stroke: 'var(--content-contentPrimary)',
            data: chartData,
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const FundingHistory: React.FC<{
  productId: string;
  timescale: 'DAY' | 'WEEK' | 'MONTH';
}> = ({ productId, timescale }) => {
  const { data: fundingRates, isLoading, error } = useFundingRates(productId, timescale);
  const { data: projectedFunding } = useProjectedFunding(productId);
  return (
    <FundingHistoryUi
      fundingRates={fundingRates ?? []}
      fundingRateProjected1h={projectedFunding?.fundingRateProjected1h}
      fundingRateProjected8h={projectedFunding?.fundingRateProjected8h}
      fundingRateProjected1d={projectedFunding?.fundingRateProjected1d}
      fundingRateProjected1y={projectedFunding?.fundingRateProjected1y}
      isLoading={isLoading}
      error={error}
      timescale={timescale}
    />
  );
};
